import React, { useRef, useState } from 'react';
import { ArrowLeftIcon, ArrowRightIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
const arrowClass = 'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 border-white sm:border-4 bg-red-700 text-white shadow-lg shadow-red-700/40 transition-[transform,background-color] duration-150 ease-out hover:scale-110 hover:bg-red-800 active:scale-95 focus:outline-none focus-visible:ring-4 focus-visible:ring-red-700/40 sm:h-14 sm:w-14';
import { pageTypes } from '../data/spremna';
import { pageSamples } from '../data/samples';
const controlClass = 'inline-flex h-12 w-12 items-center justify-center rounded-full border border-slate-900/20 text-slate-900 transition-colors duration-150 ease-out hover:bg-slate-900 hover:text-stone-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-red-700/30 disabled:pointer-events-none disabled:opacity-30';
function pad(value: number): string {
  return value.toString().padStart(2, '0');
}
export function InsidePages() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState<number>(0);
  const total = pageSamples.length;
  const goTo = (next: number) => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = Math.max(0, Math.min(total - 1, next));
    track.scrollTo({
      left: clamped * track.clientWidth,
      behavior: 'smooth'
    });
    setIndex(clamped);
  };
  const handleScroll = () => {
    const track = trackRef.current;
    if (!track || track.clientWidth === 0) return;
    const current = Math.round(track.scrollLeft / track.clientWidth);
    if (current !== index) setIndex(current);
  };
  if (total === 0) {
    return null;
  }
  return <section id="inside-pages" className="w-full border-y border-slate-900/10 bg-stone-100 px-8 py-24 sm:px-14 sm:py-32" data-id="element-188" data-mp="yxjlvft4oklo">
      <div className="mx-auto grid max-w-6xl gap-14 lg:grid-cols-12 lg:items-center" data-id="element-189" data-mp="2p73c8xnrqgqa">
        <div className="lg:col-span-4" data-id="element-190" data-mp="2ta9ii5avs7wr">
          <p className="font-serif text-2xl italic leading-none text-red-700" data-id="element-191" data-mp="623no42nsrxk">Поглед у ЛИКОВНО СПРЕМНА™</p>
          <h2 className="mt-4 font-serif text-[2.6rem] leading-[1.02] tracking-tight text-slate-900 sm:text-6xl" data-id="element-192" data-mp="13ipea6ywibr">
            Погледајте унутра <em className="text-red-700" data-id="element-193" data-mp="3t7ukydslbqts">Тома 1.</em>
          </h2>
          <p className="mt-6 text-lg leading-relaxed text-slate-900/75" data-id="element-194" data-mp="3o8i01yla4ttu">
            Праве стране из Тома 1: годишњи план, часови минут по минут и радни листови
            – направљено за директну употребу на часу.
          </p>

          <div className="mt-10 flex items-center gap-5" data-id="element-195" data-mp="ksjatoemy9yd">
            <button type="button" onClick={() => goTo(index - 1)} disabled={index === 0} aria-label="Претходна страна" className={controlClass} data-id="element-196" data-mp="g09nmg4wnnzs">
              <ArrowLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-197" data-mp="b0n7nyxoztna" />
            </button>
            <p className="font-serif text-2xl tabular-nums text-slate-900" aria-live="polite" data-id="element-198" data-mp="18hm1flr8zd50">
              {pad(index + 1)}
              <span className="text-slate-900/40" data-id="element-199" data-mp="13ostkp0hfoop"> / {pad(total)}</span>
            </p>
            <button type="button" onClick={() => goTo(index + 1)} disabled={index === total - 1} aria-label="Следећа страна" className={controlClass} data-id="element-200" data-mp="1b2th804pbu4c">
              <ArrowRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-201" data-mp="1kzogil8styjo" />
            </button>
          </div>

          <p className="mt-8 border-t border-slate-900/15 pt-5 text-sm text-slate-900/70" data-id="element-202" data-mp="1p1jfhj39bzua">
            {pageTypes.join(' · ')}
          </p>
        </div>

        <div className="min-w-0 lg:col-span-8" data-id="element-203" data-mp="1ty0ej8bzvym5">
          <div className="-mx-6 sm:mx-0" data-id="element-204" data-mp="1yyfpxucpuw1h">
          <div ref={trackRef} onScroll={handleScroll} role="group" aria-roledescription="карусел" aria-label="Примери страна из материјала" tabIndex={0} onKeyDown={event => {
            if (event.key === 'ArrowRight') {
              event.preventDefault();
              goTo(index + 1);
            }
            if (event.key === 'ArrowLeft') {
              event.preventDefault();
              goTo(index - 1);
            }
          }} className="flex w-full snap-x snap-mandatory overflow-x-auto overscroll-x-contain rounded-2xl [-ms-overflow-style:none] [scrollbar-width:none] focus:outline-none focus-visible:ring-4 focus-visible:ring-red-700/30 [&::-webkit-scrollbar]:hidden" data-id="element-205" data-mp="1yxd0zryyf3o7">
            {pageSamples.map((page, pageIndex) => <article key={page.src} aria-roledescription="слика" aria-label={`${pageIndex + 1} / ${total} – ${page.label}: ${page.title}`} className="w-full min-w-0 shrink-0 snap-start px-1 pb-6 pt-2" data-id="element-206" data-mp="2duvxmkzeejkg" data-mpi={pageIndex}>
                <div className="mx-auto grid w-full max-w-2xl grid-cols-[2.25rem_minmax(0,1fr)_2.25rem] items-center gap-x-1.5 sm:grid-cols-[3.5rem_minmax(0,1fr)_3.5rem] sm:gap-x-4" data-id="element-207" data-mp="1l5xg9q33nlg7">
                  <div className="flex justify-center" data-id="element-208" data-mp="1v3l92ah5aivq">
                    {pageIndex > 0 && <button type="button" onClick={() => goTo(pageIndex - 1)} tabIndex={pageIndex === index ? 0 : -1} aria-label="Претходна страна" className={arrowClass} data-id="element-209" data-mp="1q2fjglis7bqf">
                        <ChevronLeftIcon className="h-6 w-6 sm:h-7 sm:w-7" strokeWidth={2.75} aria-hidden="true" data-id="element-210" data-mp="31j5h3g952v8f" />
                      </button>}
                  </div>
                  <div className="aspect-[4/5] w-full overflow-hidden rounded-lg border border-slate-900/10 bg-white shadow-xl shadow-slate-900/15" data-id="element-211" data-mp="ncxqkcqx9ji7">
                    <img src={page.src} alt={`${page.label} – ${page.title}`} loading={pageIndex === 0 ? 'eager' : 'lazy'} draggable={false} className="block h-full w-full object-contain" data-id="element-212" data-mp="eay8ri832be7" />
                  </div>
                  <div className="flex justify-center" data-id="element-213" data-mp="16z3y6ia30okd">
                    {pageIndex < total - 1 && <button type="button" onClick={() => goTo(pageIndex + 1)} tabIndex={pageIndex === index ? 0 : -1} aria-label="Следећа страна" className={arrowClass} data-id="element-214" data-mp="1az4anxdmepos">
                        <ChevronRightIcon className="h-6 w-6 sm:h-7 sm:w-7" strokeWidth={2.75} aria-hidden="true" data-id="element-215" data-mp="127c3pwnt1yoy" />
                      </button>}
                  </div>
                  <div className="col-start-2 mt-5 min-w-0" data-id="element-216" data-mp="1pvlkula2py9r">
                    <p className="text-sm font-medium text-red-700" data-id="element-217" data-mp="1uoy9uwio0731">{page.label}</p>
                    <h3 className="mt-1 font-serif text-2xl leading-tight text-slate-900" data-id="element-218" data-mp="24ketbjh300na">{page.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-900/70" data-id="element-219" data-mp="1zm3ndjtwqaac">{page.text}</p>
                  </div>
                </div>
              </article>)}
          </div>
          </div>

          <div className="flex justify-center gap-2" data-id="element-220" data-mp="1plun9uwso4po">
            {pageSamples.map((page, dotIndex) => <button key={page.src} type="button" onClick={() => goTo(dotIndex)} aria-label={`Иди на страну ${dotIndex + 1}`} aria-current={dotIndex === index} className="flex h-6 items-center px-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-700/40" data-id="element-221" data-mp="1llj12dos9wc3" data-mpi={dotIndex}>
                <span className={`block h-1.5 w-6 rounded-full transition-colors duration-150 ease-out ${dotIndex === index ? 'bg-slate-900' : 'bg-slate-900/20'}`} data-id="element-222" data-mp="16wmwwrpul3gr" />
              </button>)}
          </div>
        </div>
      </div>
    </section>;
}